import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { LSThemeChanger } from './app';
import Validator, { useValidate, checkValidity } from '../../components/validator';

import LSRestedService from '../../data/lsrested-service';
import { TranslateHome, TranslateTheme } from '../../libraries/helpers';


const useStylesCreateUser = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    select: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        width: '100%',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        minHeight: 36,
    },
    chip: {
        margin: 2,
        background: theme.palette.secondary.main,
        color: '#FFF',
        fontSize: 16,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255, 255, 255, 0.75)',
        },
    },
}));

// PERMISSIONS
const ADMIN_PERMS = ['license', 'admin', 'read-user', 'update-user', 'read-audit', 'read-system', 'update-system']
const STANDARD_PERMS = ['license']

const CreateUser = () => {
    const classes = useStylesCreateUser();
    // const theme = useTheme();
    const { changeTheme, changeDateFormat } = useContext(LSThemeChanger);
    const location = useLocation();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [isUpdate, setIsUpdate] = useState(false);
    const [user, setUser] = useState({
        username: '',
        email: '',
        name: '',
        roles: [], // always empty
        permissions: _cloneDeep(STANDARD_PERMS),
        data: { profile: { home: '/', colorscheme: 'light' } },
        organization: '',
    });
    // const [roles, setRoles] = useState([]);
    const userForm = useValidate();
    // this page is always available for self - but the roles list isn't
    const [themes, setThemes] = useState(['Light', 'Dark']);
    const [init, setInit] = useState(true);
    const [valid, setValid] = useState(false);
    const [changes, setChanges] = useState(false);
    const [OGUser, setOGUser] = useState(false);

    useEffect(() => {
        if (routeParams.username) {
            setIsUpdate(true);
            LSRestedService.getUser(routeParams.username).then((res) => {
                res.object.data.profile.colorscheme = TranslateTheme(res.object.data.profile.colorscheme);
                res.object.data.profile.home = TranslateHome(res.object.data.profile.home);
                setUser(res.object);
                setOGUser(_cloneDeep(res.object));
                if (_get(res, 'object.data.profile.secret_theme', false)) {
                    const t = [...themes];
                    t.push('Pink');
                    setThemes(t);
                }
            });
        }
        setInit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Manually update select fields for now
        if (user) {
            const updatedForm = { ...userForm.get() };
            userForm.set(updatedForm);
            setTimeout(() => {
                setValid((user.email || '').length > 0 &&
                        (user.username || '').length > 0 &&
                        (user.name || '').length > 0 && checkValidity(updatedForm));
            });
        }
        setChanges(!_isEqual(user, OGUser));
    }, [user, OGUser, userForm]);

    const handleUpdateUserData = (e, validID, valid) => {
        userForm.set({ ...userForm.get(), [validID]: valid });

        setUser({
            ...user,
            [e.target.name]: e.target.value,
        });
    };

    const handleCreateUser = () => {
        if (isUpdate) {
            toast.info('Updating user...');
            LSRestedService.updateUser(routeParams.username, user).then(() => {
                toast.success('User updated successfully');
                if (location.state && location.state.fromUsers) {
                    navigate('/users', { state: { tab: 0 } });
                } else {
                    navigate(-1);
                }
            }, err => {
                if (err.status === 403) {
                    setUser({
                        ...user,
                    });
                }
                toast.error(err.error);
                console.error(err);
            });
        } else {
            toast.info('Creating user...');
            LSRestedService.createUser(user).then(() => {
                toast.success('User created successfully');
                navigate('/users', { state: { tab: 0 } });
            }, err => {
                console.error(err);
                if (err.status === 409) {
                    toast.error('Username is already taken...');
                } else {
                    toast.error(err.error);
                }
            });
        }
    };

    const handleCancel = () => {
        if (location.state && location.state.fromUsers) {
            navigate('/users', { state: { tab: 0 } });
        } else {
            navigate(-1);
        }
    };

    const handleChangePassword = () => {
        navigate('/users/password/' + user.username);
    };

    const handleUpdateIsAdmin = (e) => {
        console.log('Is Admin Update ::: ', e.target.checked);
        if (e.target.checked) {
            setUser({
                ...user,
                permissions: _cloneDeep(ADMIN_PERMS),
            });
        } else {
            setUser({
                ...user,
                permissions: _cloneDeep(STANDARD_PERMS),
            });
        }
    };

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Box mt={5}>
            <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
                <Grid item xs={12}>
                    <Paper elevation={3}>
                        <Box p={5}>
                            <Grid container spacing={5}>
                                <Grid item xs={6}>
                                    <Validator
                                        valueType="username"
                                        errorText="Username is invalid"
                                        label="Username"
                                        aria-label="username"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        kick={init}
                                        disabled={isUpdate}
                                        value={user.username}
                                        name="username"
                                        onChange={handleUpdateUserData}
                                    />
                                    <Validator
                                        valueType="email"
                                        errorText="Email is invalid"
                                        label="Email"
                                        aria-label="email"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        kick={init}
                                        required={true}
                                        allowEmpty={false}
                                        value={user.email}
                                        name="email"
                                        onChange={handleUpdateUserData}
                                    />
                                    <Validator
                                        errorText="Display name cannot be empty"
                                        label="Display Name"
                                        aria-label="display name"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        kick={init}
                                        value={user.name}
                                        name="name"
                                        onChange={handleUpdateUserData}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Validator
                                        errorText="Organization cannot be empty"
                                        label="Organization"
                                        aria-label="organization"
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        kick={init}
                                        value={user.organization}
                                        name="organization"
                                        onChange={handleUpdateUserData}
                                    />
                                    <Box mt={3}>
                                        <FormControlLabel
                                            label="Admin"
                                            labelPlacement="start"
                                            aria-label="admin"
                                            value={user.permissions}
                                            onChange={handleUpdateIsAdmin}
                                            disabled={isUpdate}
                                            control={<Checkbox checked={user.permissions.includes('admin')} />}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end">
                                        <Box ml={2} display={isUpdate ? '' : 'none'}>
                                            <Button variant="contained" color="primary" onClick={handleChangePassword}>Change Password</Button>
                                        </Box>
                                        <Box ml={2}><Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button></Box>
                                        <Box ml={2}><Button
                                            variant="contained"
                                            color="primary"
                                            aria-label={isUpdate ? 'Update' : 'Create'}
                                            onClick={handleCreateUser}
                                            disabled={!valid || !changes}
                                        >
                                            {isUpdate ? 'Update' : 'Create'}
                                        </Button></Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreateUser;
